import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Icon,
  Badge,
  Button,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Skeleton,
  Switch,
  Table,
  Tooltip,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  Title,
} from 'chart.js'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { LuBuilding2, LuChevronDown, LuChartColumn, LuTable, LuTestTube } from 'react-icons/lu'
import axiosClient from '../../api/axios'
import {
  pathologyGroupKey,
  pathologyGroupLabel,
  preferredPathologyLabel,
} from '../Lab/BlotAnalysisWorkspace/utils/displayName'

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend, Title)

type Period = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months' | 'this_year'

/**
 * What the charts count. A case is a plate someone filed; a strip is a sample
 * someone read. The two tell different stories and neither stands for the
 * other: Bordeaux files 9 plates for 299 strips where Nancy files 59 for 224,
 * so counting cases makes Bordeaux look idle and counting strips makes Nancy
 * look it. Hence a switch rather than a choice made for the reader.
 */
type Measure = 'cases' | 'strips'

const MEASURE_LABELS: Record<Measure, string> = {
  cases: 'Cas',
  strips: 'Bandelettes',
}

const PERIOD_LABELS: Record<Period, string> = {
  this_month: 'Ce mois-ci',
  last_3_months: '3 derniers mois',
  last_6_months: '6 derniers mois',
  last_12_months: '12 derniers mois',
  this_year: 'Cette année',
}

interface ChuAccount {
  id: string
  email: string | null
  name: string | null
  company: string | null
  role: string
  cases: number
}

interface ChuRow {
  profile: string
  total: number
  users: number
  strips: number
  lastCaseAt: string | null
  accounts: ChuAccount[]
  byMonth: Array<{ month: string; count: number; strips: number }>
  byPathology: Array<{ name: string; count: number; strips: number }>
}

interface ActivityByChu {
  period: Period
  includeInternal: boolean
  from: string
  to: string
  months: string[]
  chus: ChuRow[]
  pathologies: Array<{
    name: string
    total: number
    totalStrips: number
    byChu: Record<string, number>
    stripsByChu: Record<string, number>
  }>
}

/** Human label for a detection profile — the profile IS the site. */
const SITE_LABEL: Record<string, string> = {
  'chu-nancy': 'CHU Nancy',
  'chu-bordeaux': 'CHU Bordeaux',
  'chu-amiens': 'CHU Amiens',
  'chu-grenoble': 'CHU Grenoble',
  'chu-lyon': 'HC Lyon',
  aphp: 'AP-HP',
  default: 'Profil par défaut',
}
export const siteLabel = (profile: string) => SITE_LABEL[profile] ?? profile

/**
 * Colour per SITE, fixed by name and never by rank.
 *
 * A site keeps its hue when a filter changes the order, otherwise the reader
 * re-learns the chart at every click. The four steps come from the product's
 * own ramps but are not its default order: teal→cyan→blue→indigo reads as one
 * blue smear under deuteranopia (validated: ΔE 0.9 between blue and indigo,
 * far below the 8 floor). Spreading them across hue AND lightness —
 * teal-600, blue-400, violet-600, amber-500 — clears every pair at ΔE ≥ 15.
 */
const SITE_COLORS: Record<string, string> = {
  'chu-nancy': '#0d9488',
  'chu-bordeaux': '#60a5fa',
  'chu-amiens': '#7c3aed',
  aphp: '#f59e0b',
}
/** Same four steps, for a site not in the table above. Never a generated hue. */
const SPARE_COLORS = ['#f59e0b', '#0d9488', '#60a5fa', '#7c3aed']
export const siteColor = (profile: string, index: number) =>
  SITE_COLORS[profile] ?? SPARE_COLORS[index % SPARE_COLORS.length]

/** "ldbio-aspergillus-igg" → "Aspergillus IgG", for a name only ever stored as
 *  a slug. A name with spaces is already written for a human and is left be. */
const prettyLabel = (name: string): string => {
  if (/\s/.test(name) || !name.includes('-')) return name
  return name
    .replace(/^ldbio-/i, '')
    .split('-')
    .map((word) => {
      if (/^ig[gma]$/i.test(word)) return `Ig${word.slice(2).toUpperCase()}`
      if (/^[ivx]+$/i.test(word)) return word.toUpperCase()  // "ii" is a numeral
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

/**
 * The stack's own total, written at the end of each bar.
 *
 * A stacked bar is read for its height first and its composition second, and
 * the height is the one number no segment carries — the tooltip gives it, but
 * only to whoever hovers. One label per bar, never one per segment: the
 * segments stay for the tooltip and the table.
 *
 * The last VISIBLE dataset's element ends at the stack total (a zero-valued
 * segment is drawn with no width, at the cumulative position), so it carries
 * the label wherever the legend has been clicked.
 */
const stackTotals = {
  id: 'stackTotals',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart
    const horizontal = chart.options.indexAxis === 'y'
    const visible = chart.data.datasets
      .map((_: unknown, i: number) => i)
      .filter((i: number) => chart.isDatasetVisible(i))
    if (!visible.length) return
    const last = chart.getDatasetMeta(visible[visible.length - 1])
    if (!last?.data?.length) return

    ctx.save()
    ctx.fillStyle = 'rgba(74, 85, 104, 0.9)'   // gray.600 — ink, never a series colour
    ctx.font = '600 11px -apple-system, Segoe UI, Roboto, sans-serif'
    ctx.textAlign = horizontal ? 'left' : 'center'
    ctx.textBaseline = horizontal ? 'middle' : 'bottom'
    last.data.forEach((element: any, index: number) => {
      const total = visible.reduce(
        (sum: number, i: number) => sum + (Number(chart.data.datasets[i].data[index]) || 0), 0,
      )
      if (!total) return
      ctx.fillText(
        String(total),
        horizontal ? element.x + 6 : element.x,
        horizontal ? element.y : element.y - 6,
      )
    })
    ctx.restore()
  },
}

const formatMonth = (yyyyMm: string): string => {
  const [year, month] = yyyyMm.split('-')
  return new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
}

const fmtAgo = (iso: string | null) =>
  iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }) : 'jamais'

/**
 * Activity read by SITE rather than by operator.
 *
 * The pilots table next to this one answers "who works". This answers "which
 * hospital works, and on which tests" — which is what a site visit or a renewal
 * conversation needs, and what no per-user row adds up to: a case carries the
 * detection profile it was analysed with, so the site is in the data itself
 * rather than guessed from a company name.
 */
export default function ChuActivity() {
  const [period, setPeriod] = useState<Period>('last_6_months')
  const [measure, setMeasure] = useState<Measure>('cases')
  // L'activité des pilotes, par défaut. Nos propres plaques partent d'un
  // compte admin et sont des essais : comptées, elles font l'essentiel du
  // volume et les chiffres d'un site mesurent alors notre travail plutôt que
  // le sien. Derrière un interrupteur plutôt que supprimées — un panneau qui
  // cache sans le dire est pire qu'un panneau qui l'explique.
  const [includeInternal, setIncludeInternal] = useState(false)
  const [showTable, setShowTable] = useState(false)
  // Filtre par site : cliquer une tuile restreint TOUT le panneau (tuiles,
  // graphiques, tableau) aux sites cochés. Vide = tous. Multi-sélection :
  // « Nancy + Bordeaux » se compare d'un geste.
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set())
  const toggleSite = (profile: string) => {
    setSelectedSites((prev) => {
      const next = new Set(prev)
      if (next.has(profile)) next.delete(profile)
      else next.add(profile)
      return next
    })
  }

  const { data, isLoading, error } = useQuery<ActivityByChu>({
    queryKey: ['activityByChu', period, includeInternal],
    queryFn: async () => {
      const response = await axiosClient.get(
        `/lab-analysis/activity-by-chu?period=${period}&includeInternal=${includeInternal}`,
      )
      return response.data
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  // Ranked by what is being measured, not by what the server sorted on:
  // Bordeaux files 9 plates for 299 strips, so it closes the list on cases and
  // opens it on strips. Safe to reorder — a site's colour is fixed by name.
  const chus = useMemo(() => [...(data?.chus ?? [])].sort((a, b) => (measure === 'cases'
    ? b.total - a.total
    : b.strips - a.strips)), [data?.chus, measure])
  // Les sites qui COMPTENT (graphiques, totaux, tableau). Les tuiles, elles,
  // montrent toujours tous les sites — ce sont les interrupteurs du filtre.
  const visibleChus = useMemo(
    () => (selectedSites.size ? chus.filter((c) => selectedSites.has(c.profile)) : chus),
    [chus, selectedSites],
  )
  // La couleur d'un site est fixée par son rang dans la liste COMPLÈTE : elle
  // ne doit pas changer quand le filtre retire un voisin.
  const colorIndexByProfile = useMemo(() => {
    const map = new Map<string, number>()
    chus.forEach((c, i) => map.set(c.profile, i))
    return map
  }, [chus])
  const colorOf = (profile: string) => siteColor(profile, colorIndexByProfile.get(profile) ?? 0)
  const grandTotal = visibleChus.reduce((sum, c) => sum + (measure === 'cases' ? c.total : c.strips), 0)

  // Cases per month, one segment per site. Stacked rather than grouped: the
  // question is first "how much did the pilot do this month", and only then
  // "who did it" — the stack gives the total as the bar's own height.
  const monthData = useMemo(() => {
    if (!data?.months?.length || !visibleChus.length) return null
    return {
      labels: data.months.map(formatMonth),
      datasets: visibleChus.map((chu) => ({
        label: siteLabel(chu.profile),
        data: chu.byMonth.map((m) => (measure === 'cases' ? m.count : m.strips)),
        backgroundColor: colorOf(chu.profile),
        borderRadius: 4,
        borderSkipped: false,
        // A 2px gap of surface between stacked segments, so two touching
        // fills never read as one.
        borderWidth: 2,
        borderColor: 'white',
        stack: 'sites',
      })),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.months, visibleChus, measure, colorIndexByProfile])

  // Tests ranked by volume, split by site. Every test gets its own row: the
  // point of the panel is to see WHICH searches a site runs, and a test folded
  // into an "Autres" bucket is exactly the one nobody can act on. The chart
  // grows a row taller instead.
  //
  // Counted on the grouping key, not the stored name: Nancy writes the
  // catalogue name and Bordeaux what the Dynablot sheet prints, so the same
  // test would land on two rows and the ranking would say nothing.
  const pathologyRows = useMemo(() => {
    if (!data?.pathologies?.length) return []
    const grouped = new Map<string, { label: string; total: number; byChu: Record<string, number> }>()
    const pick = (p: ActivityByChu['pathologies'][number]) => (measure === 'cases'
      ? { total: p.total, byChu: p.byChu }
      : { total: p.totalStrips, byChu: p.stripsByChu })
    data.pathologies.forEach((raw) => {
      const p = { name: raw.name, ...pick(raw) }
      const key = pathologyGroupKey(p.name) || p.name
      const entry = grouped.get(key)
        ?? { label: prettyLabel(pathologyGroupLabel(p.name)), total: 0, byChu: {} }
      // Of the spellings that mean the same test, show the best written one
      // rather than whichever site happened to be counted first — see
      // preferredPathologyLabel. A group known only by a slug gets it spelled
      // out rather than shown raw.
      entry.label = preferredPathologyLabel(entry.label, prettyLabel(pathologyGroupLabel(p.name)))
      Object.entries(p.byChu).forEach(([profile, count]: [string, number]) => {
        entry.byChu[profile] = (entry.byChu[profile] ?? 0) + count
      })
      grouped.set(key, entry)
    })
    // Le total d'une ligne suit le filtre de sites : « Aspergillus chez
    // Bordeaux » doit compter le seul Bordeaux, et une recherche qu'aucun des
    // sites cochés ne pratique disparaît du classement.
    const visibleProfiles = new Set(visibleChus.map((c) => c.profile))
    return Array.from(grouped.values())
      .map((entry) => ({
        ...entry,
        total: Object.entries(entry.byChu)
          .filter(([profile]) => visibleProfiles.has(profile))
          .reduce((sum, [, count]) => sum + count, 0),
      }))
      .filter((entry) => entry.total > 0)
      .sort((a, b) => b.total - a.total)
  }, [data?.pathologies, measure, visibleChus])

  const pathologyData = useMemo(() => {
    if (!pathologyRows.length || !visibleChus.length) return null
    return {
      labels: pathologyRows.map((p) => p.label),
      datasets: visibleChus.map((chu) => ({
        label: siteLabel(chu.profile),
        data: pathologyRows.map((p) => p.byChu[chu.profile] ?? 0),
        backgroundColor: colorOf(chu.profile),
        borderRadius: 4,
        borderSkipped: false,
        borderWidth: 2,
        borderColor: 'white',
        stack: 'sites',
      })),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathologyRows, visibleChus, colorIndexByProfile])

  const chartOptions = (horizontal: boolean) => ({
    indexAxis: (horizontal ? 'y' : 'x') as 'x' | 'y',
    responsive: true,
    maintainAspectRatio: false,
    // `axis` must follow indexAxis. In index mode Chart.js matches along x
    // unless told otherwise (chart.cjs: `options.axis || 'x'`), and on a
    // horizontal bar x is the VALUE axis — the pointer picked a row by how
    // long its bar was rather than by which row it was over, so the hovered
    // breakdown belonged to another test.
    interaction: {
      mode: 'index' as const,
      intersect: false,
      axis: (horizontal ? 'y' : 'x') as 'x' | 'y',
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, padding: 16 },
      },
      tooltip: {
        // A site that ran nothing is DROPPED from the tooltip, not blanked.
        // Returning an empty label from the callback below still draws the
        // site's colour swatch, so the swatches and the figures stopped lining
        // up and the breakdown read as if it belonged to the wrong sites.
        filter: (item: any) => (item.parsed[horizontal ? 'x' : 'y'] ?? 0) > 0,
        callbacks: {
          label: (ctx: any) => ` ${ctx.dataset.label} : ${ctx.parsed[horizontal ? 'x' : 'y']}`,
          // The stack's own height, which is the first thing a stacked bar is
          // read for and the one number no segment carries.
          footer: (items: any[]) => {
            const total = items.reduce(
              (sum, i) => sum + (i.parsed[horizontal ? 'x' : 'y'] ?? 0), 0,
            )
            return items.length > 1 ? `Total : ${total}` : ''
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: horizontal, color: 'rgba(0,0,0,0.04)' },
        ticks: { precision: 0 },
        // Room at the end of the bars for the total drawn by `stackTotals`.
        grace: horizontal ? '8%' : 0,
      },
      y: {
        stacked: true,
        grid: { display: !horizontal, color: 'rgba(0,0,0,0.04)' },
        ticks: { precision: 0 },
        grace: horizontal ? 0 : '8%',
      },
    },
  })

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      boxShadow="0 10px 30px -12px rgba(15, 118, 110, 0.12)"
      border="1px solid"
      borderColor="gray.100"
      p={{ base: 5, md: 6 }}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="4px"
        bgGradient="linear(to-r, teal.400, blue.400, purple.400, orange.300)"
      />

      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={3}
        mb={5}
      >
        <HStack spacing={3}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="xl"
            bgGradient="linear(to-br, teal.400, blue.400)"
            align="center"
            justify="center"
          >
            <Icon as={LuBuilding2} color="white" boxSize={5} />
          </Flex>
          <Box>
            <Heading size="md">Activité par établissement</Heading>
            <Text fontSize="sm" color="gray.500">
              {grandTotal} {measure === 'cases' ? 'cas IRIS' : 'bandelettes'} sur la période,
              {' '}{selectedSites.size
                ? visibleChus.map((c) => siteLabel(c.profile)).join(' + ')
                : 'tous sites confondus'}
              {includeInternal ? ', comptes internes compris' : ' — pilotes uniquement'}
              {' · '}cliquez une tuile pour filtrer
            </Text>
          </Box>
        </HStack>

        <HStack spacing={3}>
          <Tooltip
            hasArrow
            placement="top"
            label="Comptabiliser aussi les cas déposés depuis les comptes admin — les plaques d'essai internes."
          >
            <HStack spacing={2}>
              <Switch
                size="sm"
                colorScheme="teal"
                isChecked={includeInternal}
                onChange={(e) => setIncludeInternal(e.target.checked)}
              />
              <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">Comptes internes</Text>
            </HStack>
          </Tooltip>
          {/* Compter des cas ou des bandelettes : deux lectures de la même
              activité, côte à côte plutôt que l'une choisie pour le lecteur. */}
          <ButtonGroup size="sm" isAttached variant="outline">
            {(Object.keys(MEASURE_LABELS) as Measure[]).map((m) => (
              <Button
                key={m}
                onClick={() => setMeasure(m)}
                bg={m === measure ? 'teal.50' : undefined}
                borderColor={m === measure ? 'teal.300' : undefined}
                color={m === measure ? 'teal.700' : 'gray.600'}
                fontWeight={m === measure ? '600' : '400'}
                zIndex={m === measure ? 1 : 0}
              >
                {MEASURE_LABELS[m]}
              </Button>
            ))}
          </ButtonGroup>
          <Menu>
            <MenuButton as={Button} size="sm" variant="outline" rightIcon={<Icon as={LuChevronDown} />}>
              {PERIOD_LABELS[period]}
            </MenuButton>
            <MenuList>
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <MenuItem key={p} onClick={() => setPeriod(p)} fontWeight={p === period ? '600' : '400'}>
                  {PERIOD_LABELS[p]}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {error && (
        <Text color="red.500" fontSize="sm">
          Impossible de charger l’activité par établissement.
        </Text>
      )}

      {isLoading && <Skeleton height="420px" borderRadius="xl" />}

      {!isLoading && !error && chus.length === 0 && (
        <VStack align="start" spacing={2}>
          <Text color="gray.500" fontSize="sm">
            {includeInternal
              ? 'Aucun cas sur la période.'
              : 'Aucune activité pilote sur la période.'}
          </Text>
          {!includeInternal && (
            <Button size="xs" variant="outline" onClick={() => setIncludeInternal(true)}>
              Inclure les comptes internes
            </Button>
          )}
        </VStack>
      )}

      {!isLoading && !error && chus.length > 0 && (
        <VStack spacing={6} align="stretch">
          {/* One tile per site: the headline numbers, which no chart replaces. */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
            {chus.map((chu) => (
              // La tuile EST le filtre : cliquée, elle restreint graphiques,
              // totaux et tableau à ce site (multi-sélection ; re-cliquer
              // libère). Les tuiles non cochées s'estompent sans disparaître —
              // ce sont les interrupteurs.
              <Box
                key={chu.profile}
                borderWidth="1px"
                borderColor={selectedSites.has(chu.profile) ? colorOf(chu.profile) : 'gray.100'}
                borderRadius="xl"
                p={4}
                position="relative"
                overflow="hidden"
                cursor="pointer"
                onClick={() => toggleSite(chu.profile)}
                opacity={selectedSites.size > 0 && !selectedSites.has(chu.profile) ? 0.45 : 1}
                boxShadow={selectedSites.has(chu.profile) ? `0 0 0 1px ${colorOf(chu.profile)}` : undefined}
                transition="opacity 0.15s, box-shadow 0.15s"
                _hover={{ opacity: 1, borderColor: colorOf(chu.profile) }}
              >
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  w="3px"
                  bg={colorOf(chu.profile)}
                />
                <HStack justify="space-between" align="start" mb={1}>
                  <Text fontSize="sm" fontWeight="600" color="gray.700">
                    {siteLabel(chu.profile)}
                  </Text>
                  <Badge colorScheme="gray" fontSize="0.65rem">
                    {/* Part du TOTAL GÉNÉRAL, pas du périmètre filtré : la part
                        d'une tuile ne doit pas gonfler quand on en éteint une autre. */}
                    {Math.round(((measure === 'cases' ? chu.total : chu.strips)
                      / Math.max(1, chus.reduce((s, c) => s + (measure === 'cases' ? c.total : c.strips), 0))) * 100)} %
                  </Badge>
                </HStack>
                {/* La tuile suit le sélecteur : le chiffre mis en avant est
                    celui qu'on compare, l'autre reste lisible en dessous. */}
                <Text fontSize="2xl" fontWeight="700" lineHeight="1.1">
                  {measure === 'cases' ? chu.total : chu.strips}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {measure === 'cases'
                    ? `cas · ${chu.strips} bandelettes`
                    : `bandelettes · ${chu.total} cas`}
                  {' · '}
                  {/* Quels comptes, pas seulement combien : un site qui montre
                      de l'activité sans compte ouvert est soit une plaque de
                      démonstration déposée depuis un compte interne, soit un
                      profil choisi par erreur — la question se pose à chaque
                      fois, autant que la tuile y réponde. */}
                  <Tooltip
                    hasArrow
                    placement="top"
                    label={
                      <VStack align="start" spacing={0.5} py={1}>
                        {chu.accounts.map((u) => (
                          <Text key={u.id} fontSize="xs">
                            {u.cases} cas · {u.email ?? u.name ?? u.id}
                            {u.role !== 'user' ? ` (${u.role})` : ''}
                          </Text>
                        ))}
                      </VStack>
                    }
                  >
                    <Text as="span" textDecoration="underline" textDecorationStyle="dotted" cursor="help">
                      {chu.users} compte{chu.users > 1 ? 's' : ''}
                    </Text>
                  </Tooltip>
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  dernier cas {fmtAgo(chu.lastCaseAt)}
                </Text>
                {chu.accounts.length > 0 && chu.accounts.every((u) => u.role !== 'user') && (
                  <Badge mt={2} colorScheme="orange" variant="subtle" fontSize="0.6rem">
                    interne uniquement
                  </Badge>
                )}
              </Box>
            ))}
          </SimpleGrid>

          {monthData && (
            <Box>
              <HStack spacing={2} mb={3}>
                <Icon as={LuChartColumn} color="teal.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  {MEASURE_LABELS[measure]} par mois et par établissement
                </Text>
              </HStack>
              <Box h="300px">
                <Bar data={monthData} options={chartOptions(false)} plugins={[stackTotals]} />
              </Box>
            </Box>
          )}

          {pathologyData && (
            <Box>
              <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
                <HStack spacing={2}>
                  <Icon as={LuTestTube} color="purple.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="600" color="gray.700">
                    Recherches les plus analysées, par établissement
                    {measure === 'strips' ? ' (en bandelettes)' : ''}
                  </Text>
                </HStack>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<Icon as={showTable ? LuChartColumn : LuTable} />}
                  onClick={() => setShowTable((v) => !v)}
                >
                  {showTable ? 'Voir le graphique' : 'Voir le tableau'}
                </Button>
              </Flex>

              {showTable ? (
                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Recherche</Th>
                        {visibleChus.map((chu) => (
                          <Th key={chu.profile} isNumeric>{siteLabel(chu.profile)}</Th>
                        ))}
                        <Th isNumeric>Total {measure === 'cases' ? 'cas' : 'band.'}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pathologyRows.map((row) => (
                        <Tr key={row.label}>
                          <Td>{row.label}</Td>
                          {visibleChus.map((chu) => (
                            <Td key={chu.profile} isNumeric color={row.byChu[chu.profile] ? 'gray.800' : 'gray.300'}>
                              {row.byChu[chu.profile] ?? 0}
                            </Td>
                          ))}
                          <Td isNumeric fontWeight="600">{row.total}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Box h={`${Math.max(260, pathologyRows.length * 38)}px`}>
                  <Bar data={pathologyData} options={chartOptions(true)} plugins={[stackTotals]} />
                </Box>
              )}
            </Box>
          )}
        </VStack>
      )}
    </Box>
  )
}
